import { useContext, useEffect, useState } from "react"
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { faEye, faPlusCircle } from "@fortawesome/free-solid-svg-icons"
import { Table, Tag } from 'antd'

import { ApplicationContext } from 'context'

import ButtonPrimary from "components/ButtonPrimary"
import SearchBar from "components/SearchBar"
import AddReportModal from "components/AddReportModal"

import api from 'lib/api'
import { discectError, formatMoney } from "lib/utils"

import styles from "./styles.module.scss"

const InventoryReports = ({ surety }) => {
  const { contextAgency, contextError, contextLoading } = useContext(ApplicationContext)
  const [isLoading, setIsLoading] = contextLoading // eslint-disable-line
  const [_, setError] = contextError // eslint-disable-line
  const [agency] = contextAgency // eslint-disable-line

  const [addReport, setAddReport] = useState(false)
  const [search, setSearch] = useState(null)
  const [reports, setReports] = useState(null)

  const navigate = useNavigate()

  const agencyId = agency.id

  useEffect(() => {
    const _fetch = async () => {
      setIsLoading(true)
      try{
        const { reports: incomingReports } = await api.reports.fetchReports()
        console.log(incomingReports)
        setReports(incomingReports)
      } catch(e) {
        setError(discectError(e))
      }
      setIsLoading(false)
    }
    if (!reports) _fetch()
  }, [reports, setError, setIsLoading])

  const _onCreateReport = async (type) => {
    setError(null)
    setIsLoading(true)
    try {
      const params = { 
        suretyId: surety.id,
        agencyId,
        reportDate: `${dayjs().format('MM/DD/YYYY')}`,
        reportName: `Report ${dayjs().format('MM/DD/YYYY')}`,
        reportId: `RPT-${dayjs().format('MMDDYYYYHHMMSS')}`,
        type
      }

      const { report: newReport } = await api.reports.createReport(agencyId, params)
      navigate(`/inventory/report/${newReport.fb_id}`)
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }

  const _getTable = () => {
    if (reports) {
      const columns = [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (_, { name }) => {
            return (
              <div className={styles.agencyInfoBold}>
                <p>{name}</p>
              </div>
            )
          }
        },
        {
          title: 'Report ID',
          dataIndex: 'identifier',
          key: 'identifier',
          width: 160,
          align: 'center',
          render: (_, { identifier }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{identifier}</p>
              </div>
            )
          }
        },
        {
          title: 'Powers',
          dataIndex: 'total',
          key: 'total',
          width: 100,
          align: 'center',
          render: (_, { powers }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{powers.length}</p>
              </div>
            )
          }
        },
        {
          title: 'Premium',
          dataIndex: 'premiumTotal',
          key: 'premiumTotal',
          width: 100,
          align: 'center',
          render: (_, { premiumTotal }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{formatMoney(premiumTotal)}</p>
              </div>
            )
          }
        },
        {
          title: 'Rate Total',
          dataIndex: 'rateTotal',
          key: 'rateTotal',
          width: 100,
          align: 'center',
          render: (_, { rateTotal }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{formatMoney(rateTotal)}</p>
              </div>
            )
          }
        },
        {
          title: 'BUF Total',
          dataIndex: 'buffTotal',
          key: 'buffTotal',
          width: 100,
          align: 'center',
          render: (_, { buffTotal }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{formatMoney(buffTotal)}</p>
              </div>
            )
          }
        },
        {
          title: 'Status',
          key: 'status',
          dataIndex: 'status',
          width: 140,
          align: 'center',
          filters: [
            {
              text: 'Generated',
              value: 'GENERATED'
            },
            {
              text: 'Printed',
              value: 'PRINTED'
            }
          ],
          onFilter: (value, record) => (record.status === value),
          render: (_, { status }) => {
            // let color = denomination.length > 5 ? 'geekblue' : 'green'; 'volcano'
            let color = 'geekblue'
            if (status.toUpperCase() === 'REVIEW') color = 'volcano'
            return (
              <Tag color={color}>
                {status.toUpperCase()}
              </Tag>
            )
          }
        },
        {
          title: 'Actions',
          key: 'action',
          width: 80,
          render: (_, record) => {
            return (
              <div className={styles.actions}>
                <ButtonPrimary small color="navy" onClick={() => navigate(`/inventory/report/${record.fb_id}`)} icon={faEye}>View Report</ButtonPrimary>     
              </div>
            )
          }
        }
      ]

      const dataSource = reports.map(itm => {
        return {
          ...itm,
          identifier: itm.identifier || ''
        }
      }).filter(itm => {
        if (search && search !== '') {
          return (
            itm.name.toLowerCase().includes(search.toLowerCase()) || itm.identifier.toLowerCase().includes(search.toLowerCase())
          )
        }
        return true
      })
      
      return (
        <Table
          bordered
          dataSource={dataSource}
          columns={columns}
          size="small"
          pagination={false}
        />
      )
    }
    return null
  }

  return (
    <>
      <div className={styles.inventoryReports}>
        <div className={styles.inventoryReports__filters}>
          <div className={styles.inventoryReports__filters__left}>
            <SearchBar text="Enter the Report Name or ID" search={search} setSearch={e => setSearch(e.target.value)} />
          </div>
          <div className={styles.inventoryReports__filters__right}>
            <ButtonPrimary small color="navy" icon={faPlusCircle} onClick={() => setAddReport(true)}>Add Report</ButtonPrimary>
          </div>
        </div>
        <div className={styles.inventoryReports__list}>
          <h5 className="rad">Reports</h5>
          <div>
            {(reports?.length > 0) && _getTable()}
            {(reports?.length === 0) && <p className={styles.noResults}>You have not entered any reports.</p>}
          </div>
        </div>
      </div>
      {addReport && <AddReportModal onSave={_onCreateReport} onClose={() => setAddReport(false)} />}
    </>
  )
}

export default InventoryReports
